import { useState } from "react";
import { toast } from "sonner";
import { Copy, Check, RefreshCw, Zap, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { BACKEND } from "@/lib/api";
import api, { formatApiErrorDetail } from "@/lib/api";

function CopyRow({ label, value, testid }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(value);
    setCopied(true);
    toast.success(`${label} copied`);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div>
      <div className="text-xs font-mono uppercase tracking-wider text-muted-foreground mb-1.5">{label}</div>
      <div className="flex items-center gap-2 rounded-md border border-emerald-500/30 bg-black/60 p-3 overflow-x-auto">
        <code className="font-mono text-xs text-emerald-400 whitespace-nowrap flex-1">{value}</code>
        <Button
          size="icon"
          variant="ghost"
          data-testid={testid}
          onClick={copy}
          className="h-7 w-7 shrink-0 hover:bg-emerald-500/10 hover:text-emerald-400"
        >
          {copied ? <Check className="h-4 w-4 text-emerald-400" /> : <Copy className="h-4 w-4" />}
        </Button>
      </div>
    </div>
  );
}

export default function EndpointCard({ sub, onChange, baseUrl }) {
  const base = (baseUrl || BACKEND).replace(/\/$/, "");
  const endpoint = `${base}/api/checkout/${sub.checkout_key}`;
  const configUrl = `${endpoint}/config`;

  const snippet = `fetch("${endpoint}", {
  method: "POST",
  headers: { "Content-Type": "application/json" },
  body: JSON.stringify({
    amount: 19.99,
    currency: "USD",
    token: cardToken,        // Square source_id or Stripe payment_method
    customer: { name: "Jane Doe", email: "jane@shop.com" },
    items: [                 // products the customer selected
      { name: "T-Shirt", quantity: 2, price: 7.50, sku: "TS-01" },
      { name: "Sticker", quantity: 1, price: 4.99 }
    ],
    shipping: {              // optional — include when the order ships
      name: "Jane Doe", phone: "+14155550100",
      line1: "123 Main St", line2: "Apt 4B",
      city: "San Francisco", state: "CA", postal_code: "94105", country: "US"
    }
  })
}).then(r => r.json());`;

  const regenerate = async () => {
    try {
      const { data } = await api.post(`/subaccounts/${sub.id}/regenerate-key`);
      toast.success("New checkout key generated");
      onChange(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const [guideCopied, setGuideCopied] = useState(false);
  const [guideBusy, setGuideBusy] = useState(false);
  const writeClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      try {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        const ok = document.execCommand("copy");
        document.body.removeChild(ta);
        return ok;
      } catch {
        return false;
      }
    }
  };
  const copyGuide = async () => {
    setGuideBusy(true);
    try {
      const { data } = await api.get("/frontend-guide");
      const guideBase = "https://paygate.revodigitaltx.com";
      const guideEndpoint = `${guideBase}/api/checkout/${sub.checkout_key}`;
      const filled = data.content
        .replace(/https:\/\/<paygate-host>\/api\/checkout\/ck_X+/g, guideEndpoint)
        .replace(/https:\/\/<paygate-host>/g, guideBase);
      const ok = await writeClipboard(filled);
      if (ok) {
        setGuideCopied(true);
        toast.success("Front-end instructions copied — paste into your AI coder");
        setTimeout(() => setGuideCopied(false), 2000);
      } else {
        toast.error("Copy blocked by the browser — select the text manually");
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not load the guide");
    } finally {
      setGuideBusy(false);
    }
  };

  return (
    <Card className="p-5 border-border/70 space-y-5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Zap className="h-4 w-4 text-primary" />
          <h3 className="font-heading font-semibold">Checkout Endpoint</h3>
        </div>
        {sub.active_provider ? (
          <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-mono font-semibold border ${sub.active_provider === "square" ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/40" : "bg-indigo-500/15 text-indigo-400 border-indigo-500/40"}`}>
            LIVE · {sub.active_provider.toUpperCase()}
          </span>
        ) : (
          <span className="px-2.5 py-0.5 rounded-full text-[10px] font-mono text-amber-400 border border-amber-500/40 bg-amber-500/10">
            NO PROVIDER
          </span>
        )}
      </div>

      <CopyRow label="POST Endpoint (give to your front-end)" value={endpoint} testid="copy-endpoint-btn" />
      <CopyRow label="Config URL (GET — provider + SDK keys)" value={configUrl} testid="copy-config-btn" />

      <div>
        <div className="text-xs font-mono uppercase tracking-wider text-muted-foreground mb-1.5">Front-end snippet</div>
        <pre className="rounded-md border border-border bg-black/60 p-3 overflow-x-auto text-xs font-mono text-muted-foreground leading-relaxed">{snippet}</pre>
      </div>

      <div className="rounded-md border border-primary/30 bg-primary/5 p-4 flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-sm font-heading font-semibold">
            <FileText className="h-4 w-4 text-primary" /> Front-end AI Instructions
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            Copy the full integration guide (with this store's endpoint pre-filled) and paste it into your AI front-end coder.
          </p>
        </div>
        <Button
          size="sm"
          data-testid="copy-frontend-guide-btn"
          onClick={copyGuide}
          disabled={guideBusy}
          className="gap-2 shrink-0"
        >
          {guideCopied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
          {guideBusy ? "Loading…" : guideCopied ? "Copied" : "Copy instructions"}
        </Button>
      </div>

      <Button
        variant="outline"
        size="sm"
        data-testid="regenerate-key-btn"
        onClick={regenerate}
        className="gap-2"
      >
        <RefreshCw className="h-3.5 w-3.5" />
        Regenerate Key
      </Button>
    </Card>
  );
}
